<template>
    <div class="container admin-table-page">
        <div class="param-title-wrap">
            <b-button class="page-back-btn mr-2" variant="link" @click="goBack">
                <font-awesome-icon :icon="['fas', 'arrow-alt-circle-left']" />
            </b-button>
            <h2 class="param-title">{{ labels.title }}</h2>
        </div>

        <div class="admin-table-block mt-4" v-if="periodosList?.length">
            <CorporateDataTable
                :items="periodosList"
                :fields="allFields"
                :filter-defs="tableFilterDefs"
                :labels="tableLabels"
                :default-per-page="10"
                :page-options="[10, 20, 50, 100]"
                :narrow="false"
                :table-min-height="500"
                :table-max-height="500"
            >
                <template #toolbar-actions>
                    <b-button size="sm" class="table-toolbar-add-btn" @click="saveAllPeriodos" :disabled="!hasChanges">
                        {{ labels.saveChanges }}
                    </b-button>
                </template>

                <template #cell(vigencia)="data">
                    <div class="text-left">{{ formatPeriodo(data.item?.vigencia) }}</div>
                </template>

                <template #cell(accionToggle)="data">
                    <b-button v-if="data.item?.enabled"
                        variant="link"
                        class="p-0 border-0 shadow-none table-action-btn"
                        @click="togglePeriodo(data.item?.vigencia, false)"
                    >
                        <i><font-awesome-icon :icon="['fas', 'toggle-on']" size="xl"/></i>
                    </b-button>
                    <b-button v-else
                        variant="link"
                        class="p-0 border-0 shadow-none table-action-btn"
                        @click="togglePeriodo(data.item?.vigencia, true)"
                    >
                        <i><font-awesome-icon :icon="['fas', 'toggle-off']" size="xl"/></i>
                    </b-button>
                </template>
            </CorporateDataTable>
        </div>

        <div class="admin-table-block mt-4" v-else>
            <h5>{{ labels.noPeriods }}</h5>
        </div>
    </div>
</template>

<script>
import Periodos from "../../services/Periodos"
import CorporateDataTable from '../utils/CorporateDataTable.vue'

export default {
    name: 'PanelAdminPeriodo',
    components: {
        CorporateDataTable
    },
    
    data() {
        return {
            periodosDisponibles: {},
            originalPeriodos: {},
            hasChanges: false,
            isLoading: false,
            periodosList: []
        }
    },
    
    computed: {
        sortedPeriodos() {
            return Object.keys(this.periodosDisponibles).sort((a, b) => Number(b) - Number(a))
        },
        
        labels() {
            if (this.$store.state._language == "es-ES") {
                return {
                    title: "Cierre de Periodo",
                    saveChanges: "Guardar Cambios",
                    vigencia: "Vigencia",
                    estado: "Estado",
                    noPeriods: "No hay vigencias disponibles",
                    noData: "No hay datos disponibles",
                    noMatches: "No se encontraron resultados",
                    searchPlaceholder: "Buscar vigencia...",
                    rowsPerPage: "Filas por página:",
                    noSort: "Sin orden",
                    sortAsc: "A-Z / Menor a mayor",
                    sortDesc: "Z-A / Mayor a menor",
                    columns: "Columnas",
                    showAllColumns: "Mostrar todas",
                    filters: "Filtros",
                    clearFilters: "Limpiar filtros",
                    filterVigencia: "Filtrar vigencia..."
                }
            } else {
                return {
                    title: "Period Lock",
                    saveChanges: "Save Changes",
                    vigencia: "Validity",
                    estado: "Status",
                    noPeriods: "No validities available",
                    noData: "No data available",
                    noMatches: "No results found",
                    searchPlaceholder: "Search validity...",
                    rowsPerPage: "Rows per page:",
                    noSort: "No sorting",
                    sortAsc: "A-Z / Low to high",
                    sortDesc: "Z-A / High to low",
                    columns: "Columns",
                    showAllColumns: "Show all",
                    filters: "Filters",
                    clearFilters: "Clear filters",
                    filterVigencia: "Filter validity..."
                }
            }
        },
        
        allFields() {
            return [
                { key: 'vigencia', label: this.labels.vigencia, sortable: true, thClass: 'text-left', tdClass: 'text-left align-middle' },
                { key: 'accionToggle', label: this.labels.estado, thClass: 'text-center', tdClass: 'text-center align-middle', sortable: false }
            ]
        },
        
        tableFilterDefs() {
            return [
                { key: 'vigencia', type: 'text', placeholder: this.labels.filterVigencia }
            ]
        },
        
        tableLabels() {
            return {
                noRow: this.labels.noData,
                noMatches: this.labels.noMatches,
                searchPlaceholder: this.labels.searchPlaceholder,
                rowsPerPage: this.labels.rowsPerPage,
                noSort: this.labels.noSort,
                sortAsc: this.labels.sortAsc,
                sortDesc: this.labels.sortDesc,
                columns: this.labels.columns,
                showAllColumns: this.labels.showAllColumns,
                filters: this.labels.filters,
                clearFilters: this.labels.clearFilters
            }
        }
    },
    
    methods: {
        goBack() {
            this.$router.back()
        },
        
        normalizePeriodo(periodo) {
            return String(periodo || '')?.replace(/\D/g, '')?.trim()
        },
        
        formatPeriodo(periodo) {
            if (!periodo) return ''
            const normalized = this.normalizePeriodo(periodo)
            if (normalized.length === 6) {
                return `${normalized?.substring(0, 4)}-${normalized?.substring(4, 6)}`
            }
            return normalized
        },
        
        async loadPeriodosFromAPI() {
            this.isLoading = true
            try {
                const response = await Periodos.getPeriodosControl()
                const periodosAPI = Array.isArray(response.data.periodos) ? response.data.periodos : []
                const periodosMap = {}
                periodosAPI.forEach((item) => {
                    const vigencia = this.normalizePeriodo(item.vigencia)
                    if (!vigencia) return
                    periodosMap[vigencia] = item.enabled !== false
                })
                
                if (Object.keys(periodosMap).length > 0) {
                    this.$store.commit('setPeriodos', periodosMap)
                    this.periodosDisponibles = { ...periodosMap }
                    this.originalPeriodos = { ...periodosMap }
                    this.hasChanges = false
                    this.updatePeriodosList()
                } else {
                    this.$store.commit('setPeriodos', {})
                    this.periodosDisponibles = {}
                    this.originalPeriodos = {}
                    this.hasChanges = false
                    this.periodosList = []
                    this.$toast.info(this.$store.state._language == "es-ES" ? 'No se encontraron vigencias en la base de datos' : 'No validities found in the database')
                }
            } catch (error) {
                console.error('Error cargando períodos:', error)
                this.$toast.error(this.$store.state._language == "es-ES" ? 'Error al cargar vigencias desde la base de datos' : 'Error loading validities from database')
            } finally {
                this.isLoading = false
            }
        },
        
        updatePeriodosList() {
            this.periodosList = this.sortedPeriodos.map(periodo => ({
                vigencia: periodo,
                enabled: this.periodosDisponibles[periodo]
            }))
        },
        
        togglePeriodo(periodo, enabled) {
            this.periodosDisponibles[periodo] = enabled
            this.hasChanges = true
            this.updatePeriodosList()
        },
        
        async saveAllPeriodos() {
            try {
                const periodos = Object.keys(this.periodosDisponibles).map((periodo) => ({
                    vigencia: this.normalizePeriodo(periodo),
                    enabled: this.periodosDisponibles[periodo]
                }))
                
                await Periodos.savePeriodosControl(periodos, this.$store.state.userId)
                this.$store.commit('setPeriodos', { ...this.periodosDisponibles })
                this.originalPeriodos = { ...this.periodosDisponibles }
                this.hasChanges = false
                
                this.$toast.success(this.$store.state._language == "es-ES" ? 'Configuración de vigencias guardada exitosamente' : 'Period configuration saved successfully')
            } catch (error) {
                console.error('Error guardando periodos:', error)
                this.$toast.error(this.$store.state._language == "es-ES" ? 'Error al guardar configuración de vigencias' : 'Error saving period configuration')
            }
        },
        
        resetChanges() {
            this.periodosDisponibles = { ...this.originalPeriodos }
            this.hasChanges = false
            this.updatePeriodosList()
            this.$toast.info(this.$store.state._language == "es-ES" ? 'Cambios reiniciados' : 'Changes reset')
        }
    },
    
    watch: {
        periodosDisponibles: {
            deep: true,
            handler() {
                const changes = Object.keys(this.periodosDisponibles).some(key => 
                    this.periodosDisponibles[key] !== this.originalPeriodos[key]
                )
                this.hasChanges = changes
            }
        }
    },
    
    mounted() {
        this.loadPeriodosFromAPI()
    }
}
</script>

<style src="../main.css"></style>